import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Users, MapPin, RefreshCw, User } from 'lucide-react';
import { fetchWithAuth, makeAbsolutePhotoUrl } from '../../../services/api';
import { cn } from '../../../utils/cn';

interface Zone {
  id: string;
  name: string;
}

interface OccupantEntry {
  person_id?: string;
  person_name?: string;
  person_type?: string;
  face_photo_url?: string;
  unit_name?: string;
  department_name?: string;
  total_seconds: number;
  last_entry?: string;
  currently_inside: boolean;
}

interface ZoneOccupants {
  zone: Zone;
  occupants: OccupantEntry[];
}

interface ZoneOccupancyViewProps {
  customerId: string;
  onBack: () => void;
  onPersonClick?: (personId: string) => void;
  lightTheme?: boolean;
}

const PERSON_TYPE_LABELS: Record<string, string> = {
  morador: 'Morador',
  resident: 'Morador',
  funcionario: 'Funcionário',
  employee: 'Funcionário',
  visitante: 'Visitante',
  visitor: 'Visitante',
  prestador: 'Prestador',
  service_provider: 'Prestador',
  proprietario: 'Proprietário',
  owner: 'Proprietário',
  staff: 'Staff',
};

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

function currentSessionSeconds(lastEntry?: string): number {
  if (!lastEntry) return 0;
  return Math.floor((Date.now() - new Date(lastEntry).getTime()) / 1000);
}

export const ZoneOccupancyView: React.FC<ZoneOccupancyViewProps> = ({
  customerId,
  onBack,
  onPersonClick,
  lightTheme = false,
}) => {
  const [zoneOccupants, setZoneOccupants] = useState<ZoneOccupants[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const totalInside = zoneOccupants.reduce((sum, z) => sum + z.occupants.length, 0);

  const fetchOccupancy = useCallback(async () => {
    setIsLoading(true);
    try {
      const zonesRes = await fetchWithAuth(`/api/v1/zones/customer/${customerId}`);
      if (!zonesRes.ok) return;
      const zonesData = await zonesRes.json();
      const zones: Zone[] = zonesData.zones || [];
      if (zones.length === 0) { setZoneOccupants([]); return; }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const results = await Promise.all(
        zones.map(async (zone) => {
          const params = new URLSearchParams({
            customer_id: customerId,
            zone_id: zone.id,
            start_date: today.toISOString(),
          });
          const res = await fetchWithAuth(`/api/v1/events/time-spent?${params}`);
          if (!res.ok) return { zone, occupants: [] as OccupantEntry[] };
          const d = await res.json();
          const entries: OccupantEntry[] = d.data || [];
          return { zone, occupants: entries.filter(e => e.currently_inside) };
        })
      );

      setZoneOccupants(results.filter(r => r.occupants.length > 0));
      setLastRefresh(new Date());
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchOccupancy();
    const interval = setInterval(fetchOccupancy, 30000);
    return () => clearInterval(interval);
  }, [fetchOccupancy]);

  return (
    <div className={cn(
      'flex flex-col h-full overflow-hidden',
      lightTheme ? 'bg-white text-gray-900' : 'bg-gray-950 text-gray-100'
    )}>
      {/* Header */}
      <div className={cn(
        'flex items-center justify-between px-4 py-3 flex-shrink-0 border-b',
        lightTheme ? 'border-gray-200' : 'border-gray-800'
      )}>
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className={cn(
              'p-1.5 rounded-lg transition-colors',
              lightTheme ? 'hover:bg-gray-100 text-gray-500' : 'hover:bg-gray-800 text-gray-400 hover:text-gray-200'
            )}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-green-500" />
            <h2 className="text-base font-semibold">Quem está dentro</h2>
          </div>
          {!isLoading && (
            <span className="px-2 py-0.5 text-xs font-semibold bg-green-500/15 text-green-500 rounded-full border border-green-500/30">
              {totalInside} {totalInside === 1 ? 'pessoa' : 'pessoas'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lastRefresh && (
            <span className={cn('text-[10px]', lightTheme ? 'text-gray-400' : 'text-gray-600')}>
              {lastRefresh.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={fetchOccupancy}
            disabled={isLoading}
            className={cn(
              'p-1.5 rounded-lg transition-colors disabled:opacity-40',
              lightTheme ? 'hover:bg-gray-100 text-gray-400' : 'hover:bg-gray-800 text-gray-500 hover:text-gray-300'
            )}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isLoading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto min-h-0 p-3 space-y-5 custom-scrollbar">
        {isLoading && zoneOccupants.length === 0 ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-5 h-5 border-2 border-gray-700 border-t-green-500 rounded-full animate-spin" />
          </div>
        ) : zoneOccupants.length === 0 ? (
          <div className={cn('flex flex-col items-center justify-center h-40', lightTheme ? 'text-gray-400' : 'text-gray-600')}>
            <Users className="h-8 w-8 mb-2 opacity-30" />
            <p className="text-sm">Nenhuma pessoa dentro no momento</p>
          </div>
        ) : (
          zoneOccupants.map(({ zone, occupants }) => (
            <div key={zone.id}>
              {/* Zone header */}
              <div className="flex items-center gap-2 mb-2 px-1">
                <MapPin className={cn('h-3 w-3 flex-shrink-0', lightTheme ? 'text-gray-400' : 'text-gray-600')} />
                <span className={cn('text-[11px] font-semibold uppercase tracking-wider', lightTheme ? 'text-gray-500' : 'text-gray-500')}>
                  {zone.name}
                </span>
                <span className={cn('text-[11px]', lightTheme ? 'text-gray-400' : 'text-gray-600')}>
                  · {occupants.length}
                </span>
              </div>

              {/* Occupant cards */}
              <div className="space-y-1.5">
                {occupants.map((person, i) => {
                  const photoUrl = person.face_photo_url
                    ? makeAbsolutePhotoUrl(person.face_photo_url)
                    : null;
                  const clickable = !!person.person_id;
                  const sessionSecs = currentSessionSeconds(person.last_entry);
                  const typeLabel = person.person_type ? PERSON_TYPE_LABELS[person.person_type] || person.person_type : null;

                  return (
                    <div
                      key={person.person_id ?? i}
                      onClick={() => clickable && person.person_id && onPersonClick?.(person.person_id)}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all',
                        lightTheme
                          ? 'bg-white border-gray-200 hover:bg-gray-50'
                          : 'bg-gray-900 border-gray-800 hover:border-gray-700',
                        clickable && 'cursor-pointer'
                      )}
                    >
                      {/* Avatar */}
                      <div className={cn(
                        'h-9 w-7 rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center border',
                        lightTheme ? 'bg-gray-100 border-gray-200' : 'bg-gray-800 border-gray-700'
                      )}>
                        {photoUrl
                          ? <img src={photoUrl} alt="" className="h-full w-full object-cover" />
                          : <User className={cn('h-4 w-4', lightTheme ? 'text-gray-400' : 'text-gray-500')} />
                        }
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{person.person_name || '—'}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {typeLabel && (
                            <span className={cn('text-[10px]', lightTheme ? 'text-gray-500' : 'text-gray-500')}>
                              {typeLabel}
                            </span>
                          )}
                          {(person.unit_name || person.department_name) && (
                            <span className={cn('text-[10px]', lightTheme ? 'text-gray-400' : 'text-gray-600')}>
                              · {person.unit_name || person.department_name}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Session time */}
                      {sessionSecs > 0 && (
                        <div className="flex-shrink-0 text-right">
                          <p className="text-xs text-green-500 font-medium tabular-nums">{formatDuration(sessionSecs)}</p>
                          <p className={cn('text-[10px]', lightTheme ? 'text-gray-400' : 'text-gray-600')}>dentro</p>
                        </div>
                      )}

                      {/* Online dot */}
                      <span className="h-1.5 w-1.5 rounded-full bg-green-500 flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
